/**
 * TanStack Query hooks — Intake Events broker-side (Chunk 9.T.3).
 *
 * Distinct de `queries/intakeLinks.ts` qui couvre les events *liés à un lien
 * d'intake F2* (9.N.3.B.0). Ici on couvre :
 *   - Upload broker multipart (POST /v1/intake-events) — crée un orphan
 *     intake_event (intake_link_id=NULL) en `pending_classification`.
 *   - Lecture du détail d'un event (GET /v1/intake-events/:id) — utilisé par
 *     la page `/intake-events/:id` pour permettre au broker de résoudre
 *     manuellement (ResolveEventDialog 9.G.2 réutilisé).
 *
 * Convention queryKey :
 *   ['intake-events', 'detail', id]
 *
 * Invalidation :
 *   - `useUploadBrokerIntake` invalide `['intake-events']` (couvre list +
 *     by-link + detail tenant-wide).
 *
 * Anti-leak FE : pas de `console.log` du token/file/event/storage_path nulle
 * part dans la chaîne queries → composants → handlers. Les erreurs HTTP sont
 * déjà bilingues (ApiError du wire) et consommées via `getApiErrorMessage`.
 */

import type {
  BrokerIntakeUploadResponse,
  ClassifyIntakeEventResponse,
  IntakeEventBrokerSanitizedRow,
  MaterializeIntakeEventInput,
  MaterializeIntakeEventResponse,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// 9.T.3 — Upload broker multipart
// -----------------------------------------------------------------------------

export type UploadBrokerIntakeResult = BrokerIntakeUploadResponse;

/**
 * POST /v1/intake-events multipart — upload broker authentifié.
 *
 * Body : `FormData` avec un ou plusieurs champs `files`. Le caller construit
 * le FormData (`fd.append("files", file)` × N). Le hook délègue à
 * `api.postMultipart` qui résout l'auth Bearer et ne force PAS `content-type`
 * (boundary auto-set par le browser).
 *
 * Retour : `{ status: "received", intake_event_id, file_ids }`. Le caller
 * peut naviguer vers `/intake-events/${intake_event_id}` pour résoudre.
 *
 * Erreurs (toutes bilingual ApiError) :
 *   - 400 INTAKE_EVENTS_UPLOAD_NO_FILES — aucun fichier dans le multipart
 *   - 400 INTAKE_EVENTS_UPLOAD_INVALID_FILES — limites/MIME/magic bytes/filename
 *   - 401 unauthorized — Bearer absent ou expiré (router redirige vers login)
 *   - 500 INTERNAL_ERROR — storage upload fail OU DB tx fail (déjà rollbacked)
 */
export function useUploadBrokerIntake() {
  const qc = useQueryClient();
  return useMutation<UploadBrokerIntakeResult, Error, FormData>({
    mutationFn: (formData) =>
      api.postMultipart<UploadBrokerIntakeResult>("/v1/intake-events", formData),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intake-events"] });
    },
  });
}

// -----------------------------------------------------------------------------
// 9.T.3 — Lecture détail event broker (sanitized payload)
// -----------------------------------------------------------------------------

/**
 * GET /v1/intake-events/:id — détail d'un event broker-sanitized (D-NEW-C).
 *
 * Payload sanitized côté backend (storage_bucket/storage_path/sha256/ip_hash
 * strippés). Anti-leak Loi 25 vérifié 9.F + 9.N.3.B.0.
 *
 * 404 → ApiException(404, INTAKE_EVENTS_NOT_FOUND) — le composant doit
 * différencier ce cas du « event existe mais en cours de chargement ».
 */
export function useIntakeEvent(id: string | undefined) {
  return useQuery({
    queryKey: ["intake-events", "detail", id],
    queryFn: () => api.get<IntakeEventBrokerSanitizedRow>(`/v1/intake-events/${id}`),
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------------
// 9.T.4 — Classify (AI inline 30s + soft-fail gracieux)
// -----------------------------------------------------------------------------

export type ClassifyIntakeEventResult = ClassifyIntakeEventResponse;

/**
 * POST /v1/intake-events/:id/classify — trigger classifier IA broker-auth.
 *
 * Comportement backend (cf. `apps/api/src/routes/intake-events.ts`) :
 *   - Success  : 200 + `classification_status: "success"` + intake_event
 *                sanitized (status → `pending_resolution` + payload.classification
 *                populé avec candidate_company/contact/kind/lang/confidence/evidence).
 *   - Soft-fail: 200 + `classification_status: "soft_fail"` +
 *                `soft_fail_reason: "timeout" | "parse_error" | "unavailable"`.
 *                Status DB reste `pending_classification` — retry autorisé.
 *   - Hard fail: 404 INTAKE_EVENTS_NOT_FOUND / 409 INTAKE_EVENTS_INVALID_TRANSITION
 *                / 403 GATING_HUMAN_REQUIRED — propagés via ApiException.
 *
 * Invalidation cache : detail event + list intake-events (Dashboard 9.P.1
 * couvre les events à classer tenant-wide).
 */
export function useClassifyIntakeEvent() {
  const qc = useQueryClient();
  return useMutation<ClassifyIntakeEventResult, Error, { eventId: string }>({
    mutationFn: ({ eventId }) =>
      api.post<ClassifyIntakeEventResult>(`/v1/intake-events/${eventId}/classify`, {}),
    onSuccess: (_data, { eventId }) => {
      void qc.invalidateQueries({ queryKey: ["intake-events", "detail", eventId] });
      void qc.invalidateQueries({ queryKey: ["intake-events", "list"] });
    },
  });
}

// -----------------------------------------------------------------------------
// 9.T.4 — Materialize (3 stratégies — attach existing project / attach company +
//                     create project / create company + create project)
// -----------------------------------------------------------------------------

export type MaterializeIntakeEventResult = MaterializeIntakeEventResponse;

/**
 * POST /v1/intake-events/:id/materialize — matérialise l'event en entités
 * persistées selon 3 stratégies (PO Q2).
 *
 * Body : `MaterializeIntakeEventInput` — validé Zod runtime (backend rejette
 * shapes invalides via bilingual codes `_MATERIALIZE_STRATEGY_INVALID` 422 +
 * `_MATERIALIZE_CONFIRM_REQUIRED` 422). `confirmed_create` obligatoire pour
 * stratégies 2+3 (PO Q3).
 *
 * Invalidation cache large : ["intake-events"] + ["companies"] + ["projects"]
 * — materialize peut créer une company et/ou un project, potentiellement
 * consommés par d'autres surfaces (listes, dashboard, wizard).
 */
export function useMaterializeIntakeEvent() {
  const qc = useQueryClient();
  return useMutation<
    MaterializeIntakeEventResult,
    Error,
    { eventId: string; input: MaterializeIntakeEventInput }
  >({
    mutationFn: ({ eventId, input }) =>
      api.post<MaterializeIntakeEventResult>(
        `/v1/intake-events/${eventId}/materialize`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intake-events"] });
      void qc.invalidateQueries({ queryKey: ["companies"] });
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
